import type {
  NrtRecordsResponseBody,
  NrtRuleListResult
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import {
  buildInitialNrtRuleListResult,
  buildNrtRuleListCounts
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import {
  NRT_LAMBDA_BASE_PATH,
  normalizeNrtRecord,
  readErrorMessage,
  repeatedCursorMessage
} from "./ddc-pages-nrt-database-filter-tool-fetch";
import { RepeatedCursorError, walkCursorPages } from "./cursorPagination";

// API Gateway caps limit at 500.
const maxPageSize = 500;

const getNrtRulesPage = async (
  accountId: string,
  cursor: string | undefined,
  signal: AbortSignal | undefined
): Promise<{ response: Response; body: NrtRecordsResponseBody | null }> => {
  const params = new URLSearchParams({
    dealerAccount: accountId,
    limit: String(maxPageSize)
  });
  if (cursor) {
    params.set("cursor", cursor);
  }

  const response = await fetch(
    `${NRT_LAMBDA_BASE_PATH}/getAllRecords?${params.toString()}`,
    { signal }
  );

  if (!response.ok) {
    return { response, body: null };
  }
  return { response, body: (await response.json()) as NrtRecordsResponseBody };
};

// Carries the exact user-facing message through the generic walker/catch so
// it reaches the ERROR result unprefixed, same as before this was extracted.
class NrtRulesPageError extends Error {}

// Filters by providerId client-side: the table has no provider-level index.
// Never rejects — every failure resolves to an ERROR result.
export const fetchNrtRulesForAccountAndProvider = async (
  accountId: string,
  providerId: string,
  signal?: AbortSignal
): Promise<NrtRuleListResult> => {
  try {
    const allItems = await walkCursorPages<
      NrtRecordsResponseBody["items"][number]
    >(async cursor => {
      const { response, body } = await getNrtRulesPage(
        accountId,
        cursor,
        signal
      );

      if (signal?.aborted) {
        // Message is irrelevant: the catch below checks aborted first.
        throw new Error("aborted");
      }

      if (!response.ok || !body) {
        const message = await readErrorMessage(
          response,
          "Failed to fetch rules for this account/provider"
        );
        throw new NrtRulesPageError(message);
      }

      return { items: body.items, nextCursor: body.pagination.nextCursor };
    }, repeatedCursorMessage);

    if (signal?.aborted) {
      return buildInitialNrtRuleListResult();
    }

    const rules = allItems
      .map(normalizeNrtRecord)
      .filter(rule => rule.providerId === providerId);

    return {
      status: "SUCCESS",
      rules,
      counts: buildNrtRuleListCounts(rules),
      message: null
    };
  } catch (error) {
    if (signal?.aborted) {
      return buildInitialNrtRuleListResult();
    }
    if (error instanceof RepeatedCursorError) {
      return {
        status: "ERROR",
        rules: [],
        counts: null,
        message: repeatedCursorMessage
      };
    }
    if (error instanceof NrtRulesPageError) {
      return {
        status: "ERROR",
        rules: [],
        counts: null,
        message: error.message
      };
    }
    const message = error instanceof Error ? error.message : "unknown error";
    return {
      status: "ERROR",
      rules: [],
      counts: null,
      message: `Could not fetch rules for this account/provider: ${message}`
    };
  }
};
